import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import Input from 'components/form/input/Input';
import {
  Body,
  Error as ErrorMessage,
  Title2,
} from 'components/typography/Typography';
import { UPDATE_ACCOUNT_SETTINGS } from 'graphql/users/mutations';
import { GET_USER } from 'graphql/users/queries';
import { useQuery } from 'hooks/sympl-query';
import useUnsavedFields from 'hooks/unsaved';
import { Controller, useForm } from 'react-hook-form';
import Container from 'components/container/Container';
import ContainerHeader from 'components/container/container-header/ContainerHeader';
import ContainerAction from 'components/container/container-action/ContainerAction';
import SaveButton from 'components/save-button/SaveButton';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import useBeforeUnloadWarning from 'hooks/useBeforeUnloadWarning';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import {
  ACCOUNT_PATH,
  missingForNameCard,
  nameCardBody,
  saveNameCard,
} from 'api/accountAutoSave';

import { AccountSettingsForm } from '../AccountSettings';

import 'twin.macro';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import {
  AccountSettingsData,
  AccountSettingsFormData,
  buildAccountSettingsData,
  getAccountSettingsErrorMessage,
  getPasswordSettingsError,
} from './settingsPayload';

interface AccountSettingsPayload {
  input: AccountSettingsData;
}

type UserData = {
  firstname: string;
  lastname: string;
  job_position: string;
  signature: string;
};

const isPasswordField = (str: string): boolean => str.includes('password');

const toTitleCase = (str: string) => {
  return str
    .replace(/-/g, ' ')
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const GenericSettings: React.FC<{
  title: string;
  fields: AccountSettingsForm[];
}> = ({ title, fields }) => {
  const formMethods = useForm();
  const formRef = useRef<HTMLFormElement>(null);
  const { control, getValues, handleSubmit, setValue } = formMethods;
  const { addToast } = useToastNotifications();

  const [shouldSaveChanges, resetUnsavedFlag] = useUnsavedFields(formMethods);

  const {
    data: userData,
    loading: loadingUserData,
    refetch: refetchUserData,
  } = useQuery<{
    user: UserData;
  }>(GET_USER, {
    /*
     * Auto-save: always from the server, never the app's remembered copy.
     * The card's own store does not refresh that copy, so on coming back to
     * this screen the boxes showed the values from the FIRST visit, and the
     * next keystroke stored them over what had been saved since (review of
     * the slice).
     */
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  useEffect(() => {
    setValue('firstname', userData?.user.firstname);
    setValue('lastname', userData?.user.lastname);
    setValue('job-position', userData?.user.job_position);
    setValue('signature-job-position', userData?.user.signature);
  }, [userData, setValue]);

  const [
    updateAccountSettings,
    {
      loading: accountSettingsLoading,
      error: accountSettingsError,
      reset: resetAccountSettings,
    },
  ] = useMutation<undefined, AccountSettingsPayload>(UPDATE_ACCOUNT_SETTINGS);

  const isPasswordSettings = fields.some(isPasswordField);

  /*
   * Auto-save. The name card stores itself; the password card keeps its
   * button (a password changes when "Change password" is pressed, scene 16).
   *
   * The name card is one of the four screens without a publish moment
   * (D34): while the first or the last name is empty NOTHING of the card is
   * stored, the corner says why, and leaving says the card goes back to how
   * it was last saved (D35).
   */
  const storesItself =
    !isPasswordSettings && !!userData?.user && !loadingUserData;
  const storesItselfNow = useRef(false);
  storesItselfNow.current = storesItself;
  /** What the person typed, per field, since the card was loaded. */
  const [typed, setTyped] = useState<AccountSettingsFormData>({});
  /** Raised to draw the boxes again from the card's values (Undo). */
  const [boxesKey, setBoxesKey] = useState(0);
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const loaded = useMemo<AccountSettingsFormData | undefined>(
    () =>
      userData?.user
        ? {
            firstname: userData.user.firstname ?? '',
            lastname: userData.user.lastname ?? '',
            'job-position': userData.user.job_position ?? '',
            'signature-job-position': userData.user.signature ?? '',
          }
        : undefined,
    [userData]
  );
  const work = useMemo<AccountSettingsFormData | undefined>(() => {
    if (!storesItself || !loaded) return undefined;
    const now: AccountSettingsFormData = {};
    fields.forEach((field) => {
      now[field] = typed[field] ?? loaded[field] ?? '';
    });
    return now;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesItself, loaded, typed]);

  const missing = work ? missingForNameCard(work) : [];
  const incomplete = missing.length > 0;
  /** What the server holds: what was loaded, then what was last stored. */
  const storedWork = useRef<AccountSettingsFormData>();
  if (work && !storedWork.current) storedWork.current = work;
  // While something is missing the saver is handed what is already stored:
  // nothing goes out, and a store that was waiting is called off.
  const storable = work ? (incomplete ? storedWork.current : work) : undefined;
  const heldBack =
    incomplete &&
    !!work &&
    JSON.stringify(work) !== JSON.stringify(storedWork.current);
  const heldBackRef = useRef(heldBack);
  heldBackRef.current = heldBack;

  // D10: the count of stores this screen builds on (a person's own settings).
  const version = useKeptVersion('account', 0);

  const storeNameCard = useCallback(
    async (
      value: AccountSettingsFormData,
      signal?: AbortSignal
    ): Promise<string> => {
      await saveNameCard(fields, value, signal, {
        basedOn: version.basedOn(0),
        stored: (count) => version.stored(0, count),
      });
      storedWork.current = value;
      return new Date().toISOString();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<AccountSettingsFormData>({
    value: storable,
    enabled: storesItself && !!storable,
    identity: storesItself ? 'account-name-card' : null,
    save: storeNameCard,
    flushOnHide: (value) =>
      sendKeepaliveSave(
        ACCOUNT_PATH,
        nameCardBody(fields, value),
        'POST',
        version.basedOn(0)
      ),
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo<AccountSettingsFormData>(
    {
      value: work,
      enabled: storesItself,
      identity: storesItself ? 'account-name-card' : null,
      apply: (earlier) => {
        setTyped(earlier);
        Object.entries(earlier).forEach(([field, value]) =>
          setValue(field, value)
        );
        // The boxes start from the card's values: drawn again.
        setBoxesKey((n) => n + 1);
      },
    }
  );

  useBeforeUnloadWarning(storesItself && heldBack);

  useEffect(() => {
    if (!storesItself) return undefined;
    setPending(
      (hasPending && hasFailed) || heldBack,
      heldBack ? missing[0] : undefined
    );
    registerAbandon(abandon);
    registerSaveNow(
      () => (heldBackRef.current ? Promise.resolve(false) : saveNow()),
      () => !heldBackRef.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storesItself,
    hasPending,
    hasFailed,
    heldBack,
    missing[0],
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);
  const [passwordSettingsError, setPasswordSettingsError] = useState<string>();
  const accountSettingsErrorMessage = getAccountSettingsErrorMessage(
    accountSettingsError,
    isPasswordSettings
  );
  const settingsErrorMessage =
    passwordSettingsError ?? accountSettingsErrorMessage;

  const getSubmittedData = (
    data: AccountSettingsFormData
  ): AccountSettingsFormData => {
    if (!formRef.current) return data;

    const submittedFormData = new FormData(formRef.current);
    return fields.reduce<AccountSettingsFormData>(
      (submittedData, field) => ({
        ...submittedData,
        [field]: submittedFormData.get(field)?.toString() ?? data[field],
      }),
      data
    );
  };

  const submitHandler = async (data: AccountSettingsFormData) => {
    const submittedData = getSubmittedData(data);
    const validationError = isPasswordSettings
      ? getPasswordSettingsError(submittedData)
      : undefined;

    setPasswordSettingsError(validationError);
    if (validationError) return;

    try {
      await updateAccountSettings({
        variables: {
          input: buildAccountSettingsData(fields, submittedData),
        },
      });
      addToast({
        type: ToastTypes.SUCCESS,
        description: 'Your settings have been successfully updated',
      });
      refetchUserData();
    } catch (_error) {
      // Apollo exposes the server error through accountSettingsError.
    }
  };

  const isRequiredField = (key: AccountSettingsForm) => {
    return key.includes('firstname') || key.includes('lastname');
  };

  function getDefaultValue(key: AccountSettingsForm): string | number {
    // The card's own value when it stores itself (so that Undo can draw the
    // boxes again from it).
    if (work && work[key] !== undefined) return work[key] as string;
    const defaultValues: Record<AccountSettingsForm, string | number> = {
      'new-password': '',
      'repeat-password': '',
      password: '',
      'job-position': userData?.user.job_position ?? '',
      firstname: userData?.user.firstname ?? '',
      lastname: userData?.user.lastname ?? '',
      'signature-job-position': userData?.user.signature ?? '',
    };

    return defaultValues[key] ?? '';
  }

  const getAutoComplete = (field: AccountSettingsForm) => {
    if (field === 'password') return 'current-password';
    if (field === 'new-password' || field === 'repeat-password')
      return 'new-password';

    return undefined;
  };

  /*
   * What is in the boxes, read from the boxes themselves at every keystroke.
   *
   * The boxes' own "changed" signal cannot be relied on here: a box is
   * filled in after it was drawn (the user's data arrives later), and
   * emptying such a box before typing anything else in it gives no signal
   * at all (seen in the first browser run: an emptied last name went
   * unnoticed, and the rest of the card was stored). The Save button read
   * the boxes directly for the same reason (`getSubmittedData`).
   */
  const readTheBoxes = () => {
    // Not before the card's own values are in: boxes read while still
    // empty would count as "emptied by the person" (review of the slice).
    if (isPasswordSettings || !storesItselfNow.current || !formRef.current) {
      return;
    }
    const boxes = new FormData(formRef.current);
    setTyped((before) => {
      const now: AccountSettingsFormData = { ...before };
      fields.forEach((field) => {
        const value = boxes.get(field);
        if (typeof value === 'string') now[field] = value;
      });
      return JSON.stringify(now) === JSON.stringify(before) ? before : now;
    });
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    onChange(event);
    readTheBoxes();
    setPasswordSettingsError(undefined);
    if (accountSettingsError) resetAccountSettings();
  };

  const validatePasswordBeforeSave = async () => {
    if (!isPasswordSettings) return;

    const validationError = getPasswordSettingsError(
      getSubmittedData(getValues() as AccountSettingsFormData)
    );
    setPasswordSettingsError(validationError);

    if (validationError) throw new Error(validationError);
  };

  return (
    <Container>
      <form
        ref={formRef}
        onSubmit={handleSubmit(submitHandler)}
        onInput={readTheBoxes}
        autoComplete="on"
      >
        <>
          <ContainerHeader>
            <Title2 mb={3}>{title}</Title2>
          </ContainerHeader>

          <div tw="mt-1 flex flex-col gap-3 md:flex-row" key={boxesKey}>
            {fields.map((field: AccountSettingsForm, i) => {
              const title = toTitleCase(field);
              return (
                <div tw="flex-1" key={i}>
                  <Body mb={1}>{title}</Body>
                  <Controller
                    id={field}
                    name={field}
                    control={control}
                    defaultValue={getDefaultValue(field)}
                    render={({ onChange, value }) => {
                      return (
                        <Input
                          type={isPasswordField(field) ? 'password' : 'text'}
                          id={field}
                          name={field}
                          required={isRequiredField(field)}
                          defaultValue={value}
                          autoComplete={getAutoComplete(field)}
                          placeholder={title}
                          onChange={(event) =>
                            handleFieldChange(event, onChange)
                          }
                        />
                      );
                    }}
                  />
                </div>
              );
            })}
          </div>
          {isPasswordSettings ? (
            <div tw="mt-2 min-h-[20px]" aria-live="polite">
              {settingsErrorMessage && (
                <ErrorMessage mt={0}>{settingsErrorMessage}</ErrorMessage>
              )}
            </div>
          ) : (
            settingsErrorMessage && (
              <ErrorMessage>{settingsErrorMessage}</ErrorMessage>
            )
          )}
        </>
        <ContainerAction>
          <div tw="ml-auto flex flex-row items-center gap-2">
            {storesItself && (
              <>
                <UndoRedo
                  canUndo={canUndo}
                  canRedo={canRedo}
                  onUndo={undo}
                  onRedo={redo}
                />
                <OutOfDateBox
                  refusal={refusal}
                  onLoadTheirs={() => {
                    abandon();
                    window.setTimeout(() => window.location.reload(), 100);
                  }}
                  onKeepMine={() => {
                    version.keepMine();
                    retryRefused();
                  }}
                />
                <SaveStatus
                  state={saveState}
                  keptAt={keptAt}
                  switching={switching}
                  notSavedBecause={
                    heldBack ? `Not saved: ${missing[0]}` : undefined
                  }
                />
              </>
            )}
            {!storesItself && (
              <SaveButton
                type="submit"
                loading={accountSettingsLoading}
                // The password card says nothing about "unsaved changes":
                // next to a card that stores itself the line read as if the
                // name was not stored (Steven, 6 Oct 2026).
                shouldSave={!isPasswordSettings && shouldSaveChanges}
                error={Boolean(passwordSettingsError) || accountSettingsError}
                labelError={
                  passwordSettingsError
                    ? 'Some fields are incorrect'
                    : isPasswordSettings
                    ? 'Password not changed'
                    : 'Settings not saved'
                }
                onClick={validatePasswordBeforeSave}
                onDone={resetUnsavedFlag}
              />
            )}
          </div>
        </ContainerAction>
      </form>
    </Container>
  );
};
